import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { BsChevronLeft, BsInfoCircle, BsLock } from 'react-icons/bs';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { Drawer, DrawerBody } from '../../../../v2/js/components/drawer';
import { StatusCodes } from '../../../../v2/js/constants/workflows';
import { ConnectionDrawer } from '../../../../v2/js/pages/Organization/Connections/ConnectionDrawer';
import { TRANSITION_ALL } from '../../../../v2/js/utils/transitionStyles';
import { ConnectionsService, ProviderService } from '../../../api';
import { Card } from '../../../app/components/Card';
import { ConnectedFacilitiesList } from '../../../app/components/ConnectedFacilitiesList';
import { ProviderProfileHeader } from '../../../app/components/ProviderProfileHeader';
import { TabsNavbar } from '../../../app/components/TabsNavbar';
import { SessionContext } from '../../../app/contexts/SessionContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { connectionRequestsQueryKeys } from '../../../app/query-keys-factories/connection-requests';
import { healthcareEntityFacilityQueryKeys } from '../../../app/query-keys-factories/facilities';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { CreateConnectionDialog } from './CreateConnectionDialog';

const ConnectButton = ({
  user,
  isLoading,
  hasConnection,
  onCreateConnection,
  onAddFacility,
}: {
  user: any;
  isLoading: boolean;
  hasConnection: boolean;
  onCreateConnection: () => void;
  onAddFacility: () => void;
}) => {
  if (!user?.group_names?.includes('connect_with_providers') || isLoading) {
    return null;
  }

  return (
    <Col
      className="d-flex gap-3 align-items-center justify-content-end"
      sm={{ order: 1, span: 12 }}
      md={{ order: 2, span: 'auto' }}
    >
      {hasConnection ? (
        <Button size="lg" className="p-3 w-100" onClick={onAddFacility}>
          Connect
        </Button>
      ) : (
        <Button size="lg" className="p-3 w-100" onClick={onCreateConnection}>
          Connect
        </Button>
      )}
    </Col>
  );
};

const ProviderTabs = ({
  provider,
  providerPermissions,
}: {
  provider: any;
  providerPermissions: any;
}) => (
  <TabsNavbar
    alignment="center"
    tabs={[
      {
        label: 'Calendar Overview',
        to: `/app/healthcare-entity/providers/${provider.id}`,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Credentials</>,
        to: `/app/healthcare-entity/providers/${provider.id}/credentials`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Professional Traits</>,
        to: `/app/healthcare-entity/providers/${provider.id}/professional-traits`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Education</>,
        to: `/app/healthcare-entity/providers/${provider.id}/education`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: (
          <>{!providerPermissions?.retrieve.details && <BsLock />} Licenses & Certifications</>
        ),
        to: `/app/healthcare-entity/providers/${provider.id}/licenses`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Other</>,
        to: `/app/healthcare-entity/providers/${provider.id}/other`,
        disabled: !providerPermissions?.retrieve.details,
      },
    ]}
  />
);

export const ProviderProfile = () => {
  useDocumentTitle('Provider Profile');
  const { providerId: providerIdOrUndefined } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(SessionContext);
  const [showCreateConnectionDialog, setShowCreateConnectionDialog] = useState(false);
  const [showAddFacilityConnectionDialog, setShowAddFacilityConnectionDialog] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  const providerId = providerIdOrUndefined ?? '';

  const {
    data: providerPermissions,
    isLoading: isProviderPermissionsLoading,
    error: permissionsFetchError,
  } = useQuery({
    queryKey: providerQueryKeys.permissions(providerId),
    queryFn: async () =>
      await ProviderService.providerPermissionsRetrieve({
        providerId,
      }),
    enabled: !!providerId,
  });

  const {
    isLoading: isProviderLimitedLoading,
    error: providerLimitedFetchError,
    data: providerLimited,
  } = useQuery({
    queryKey: providerQueryKeys.limitedDetail(providerId),
    queryFn: async () =>
      await ProviderService.providerLimitedRetrieve({
        providerId: providerId,
      }),
    enabled:
      providerPermissions?.retrieve.details === false &&
      providerPermissions?.retrieve.limited_details,
  });

  const {
    isLoading: isProviderFullLoading,
    error: providerFullFetchError,
    data: providerFull,
  } = useQuery({
    queryKey: providerQueryKeys.detail(providerId),
    queryFn: async () =>
      await ProviderService.providerRetrieve({
        providerId: providerId,
      }),
    enabled: providerPermissions?.retrieve.details,
  });

  const connectionQueryParams: Parameters<
    typeof ConnectionsService.connectionsConnectedFacilityList
  >[0] = {
    provider: providerId,
    ordering: 'facility__name',
  };
  const { data: connectionData, isLoading: isConnectionDataLoading } = useQuery({
    queryKey: connectionRequestsQueryKeys.list(connectionQueryParams),
    queryFn: async () =>
      await ConnectionsService.connectionsConnectedFacilityList(connectionQueryParams),
    enabled: !!providerId,
  });

  const provider = providerPermissions?.retrieve.details ? providerFull : providerLimited;
  const isProviderLoading = providerPermissions?.retrieve.details
    ? isProviderFullLoading
    : isProviderLimitedLoading;
  const providerFetchError = providerPermissions?.retrieve.details
    ? providerFullFetchError
    : providerLimitedFetchError;

  const hasConnection = useMemo(() => {
    if (isConnectionDataLoading || !connectionData?.results) {
      return false;
    }
    return connectionData.results.length > 0;
  }, [connectionData?.results, isConnectionDataLoading]);

  useEffect(() => {
    if (!providerIdOrUndefined) {
      navigate('/app');
    }
  }, [providerIdOrUndefined, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const queryClient = useQueryClient();
  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
      queryClient.invalidateQueries(providerQueryKeys.allLists()),
      queryClient.invalidateQueries(providerQueryKeys.allDetails()),
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists()),
    ]);
  }, [queryClient, providerId]);

  return (
    <>
      <div
        className={clsx(
          `tw:flex tw:min-h-0 ${TRANSITION_ALL}`,
          selectedConnectionId && 'tw:h-[calc(100vh-72px)] tw:overflow-hidden'
        )}
      >
        <div
          className={clsx(
            `tw:flex-1 tw:min-w-0 ${TRANSITION_ALL}`,
            selectedConnectionId
              ? 'tw:w-3/5 tw:lg:w-3/5 tw:overflow-y-auto'
              : 'tw:w-full tw:overflow-hidden'
          )}
        >
          <div style={{ position: 'relative' }}>
            <div
              className="bg-primary-dark d-block"
              style={{
                height: 'calc(3rem + 120px)',
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                zIndex: -10,
              }}
            />
            <Container>
              <Row>
                <Col xs={12}>
                  {!isProviderLoading &&
                    !isProviderPermissionsLoading &&
                    (!!providerFetchError || !!permissionsFetchError) && (
                      <Row className="row-cols-auto justify-content-center">
                        <Col>
                          <Alert variant="danger" show={!!providerFetchError}>
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p>
                              Please try again later. If the problem persists, please contact us.
                            </p>
                          </Alert>
                        </Col>
                      </Row>
                    )}
                  <ProviderProfileHeader
                    provider={provider}
                    isProviderLoading={isProviderPermissionsLoading || isProviderLoading}
                    hasAcceptedConnection={
                      (provider?.clearance_status &&
                        provider?.clearance_status !== StatusCodes.NOT_CLEARED) ||
                      false
                    }
                    callToAction={
                      <Row className="h-100 d-flex gap-3 align-items-center justify-content-xl-end pb-3">
                        <ConnectButton
                          user={user}
                          isLoading={isProviderLoading || isProviderPermissionsLoading}
                          hasConnection={hasConnection ?? false}
                          onCreateConnection={() => setShowCreateConnectionDialog(true)}
                          onAddFacility={() => setShowAddFacilityConnectionDialog(true)}
                        />
                      </Row>
                    }
                  />
                  {!!provider &&
                    !isProviderPermissionsLoading &&
                    providerPermissions?.retrieve.limited_details && (
                      <ConnectedFacilitiesList
                        providerId={provider.id}
                        onConnectionSelect={setSelectedConnectionId}
                      />
                    )}
                  <Card className="mb-4">
                    {(isProviderLoading || isProviderPermissionsLoading) && (
                      <Row>
                        <Col xs={12} className="d-flex align-items-center justify-content-center">
                          <Spinner
                            aria-label="loading indicator"
                            animation="grow"
                            className="loading-spinner"
                            variant="primary"
                          />
                        </Col>
                      </Row>
                    )}

                    {!!provider && (
                      <>
                        <ProviderTabs
                          provider={provider}
                          providerPermissions={providerPermissions}
                        />
                        <div className="d-flex flex-column position-relative">
                          <Outlet />
                          {!providerPermissions?.retrieve.details && (
                            <div className="d-flex flex-column position-absolute connection-screen justify-content-center align-items-center">
                              <div className="text-center connection-screen--text">
                                <BsInfoCircle size={48} className="mb-3 mx-auto" />
                                <div className="fs-4 mb-3 pb-2">
                                  Create connection with a provider to view a provider&apos;s
                                  availability!
                                  <ConnectButton
                                    user={user}
                                    isLoading={isProviderLoading || isProviderPermissionsLoading}
                                    hasConnection={hasConnection ?? false}
                                    onCreateConnection={() => setShowCreateConnectionDialog(true)}
                                    onAddFacility={() => setShowAddFacilityConnectionDialog(true)}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        </div>

        {selectedConnectionId && providerPermissions?.retrieve.limited_details && (
          <Drawer
            open={!!selectedConnectionId}
            onClose={() => setSelectedConnectionId(null)}
            width="3xl"
            mode="side-by-side"
            className={TRANSITION_ALL}
          >
            <DrawerBody className="tw:!text-sm/6">
              <ConnectionDrawer
                key={selectedConnectionId}
                connectionId={selectedConnectionId}
                onClose={() => setSelectedConnectionId(null)}
              />
            </DrawerBody>
          </Drawer>
        )}
      </div>
      {!isConnectionDataLoading && !!provider && (
        <>
          <CreateConnectionDialog
            provider={provider}
            isDialogOpen={showCreateConnectionDialog}
            closeDialog={() => setShowCreateConnectionDialog(false)}
            hasConnection={false}
          />
          <CreateConnectionDialog
            provider={provider}
            isDialogOpen={showAddFacilityConnectionDialog}
            closeDialog={() => setShowAddFacilityConnectionDialog(false)}
            hasConnection={true}
          />
        </>
      )}
    </>
  );
};
